import React, { FC, useState } from 'react';
import styles from '../EnvelopeItem.module.scss';

import { useAllowedActions } from '@hooks/envelopes/useAllowedActions';
import { useLoadEnvelopes } from '@hooks/envelopes/useLoadEnvelopes';

import { useTranslation } from 'react-i18next';

import {
  ComboButton,
  ILibDropdownItem,
  ILibHorizontalPosition,
} from '@infologistics/frontend-libraries';

import {
  checkAllowedActions,
  getAllowedAction,
  getOptionsAllowedAction,
  isSingleEnvelopeMassActionAvailable,
} from '@views/envelopes/utils';
import { actionButtonSettings } from '@views/envelopes/consts';

import { IEnvelopeActionsProps as IProps } from '@views/envelopes/types';
import {
  catchThunkError,
  displayErrorNotification,
  getEnvelopeQualifiedSignature,
} from '@app/utils/utils';
import { EnvelopeAllovedActions, EnvelopeAllowedActionsButton, EnvelopeDirection } from '@app/const/consts';
import { useAppDispatch, useAppSelector } from '@app/hooks/redux';
import { getEnvelope, setEnvelopeReadState } from '@app/store/modules/envelopes';
import { EnvelopeMassActionModal } from '../../EnvelopeMassActionModal';
import TasksService from '@services/tasks';

const MARK_AS_READ_ACTION = 'MARK_AS_READ';

const EnvelopeActions: FC<IProps> = ({
  envelope,
  allowedActions,
  oguid,
  subject,
  boxOguid
}) => {
  const { t } = useTranslation(['envelopes', 'crypto']);
  const { handleClickAllowedActions } = useAllowedActions();
  const { loadEnvelopes } = useLoadEnvelopes();
  const [processingAction, setProcessingAction] = useState(false);
  const [selectedMassAction, setSelectedMassAction] = useState<EnvelopeAllovedActions | null>(null);

  const checkedAllowedActions = checkAllowedActions(allowedActions);
  const allowedActionValue = getAllowedAction(checkedAllowedActions);
  const { title, theme } = actionButtonSettings[allowedActionValue];

  const currentUserBoxOguid =
    useAppSelector((state) => state.boxes.boxes).find(
      (val) => val.oguid === boxOguid
    )?.oguid ?? '';

  const { instance } = useAppSelector((state) => state.utils);
  const { direction } = useAppSelector((state) => state.envelopes.envelopes);
  const envelopeDirection = envelope.direction ?? direction;
  const dispatch = useAppDispatch();

  const isRowSignMassActionAvailable =
    isSingleEnvelopeMassActionAvailable(
      envelope,
      EnvelopeAllovedActions.SIGN,
      instance
    );

  const actionDropdownItems = getOptionsAllowedAction(
    false,
    checkedAllowedActions,
    direction === EnvelopeDirection.OUTGOING
  ).map((item) => {
    const { translation = '' } = item;

    return {
      ...item,
      translation: t(translation),
    };
  });
  const dropdownItems = envelope.currentUserTaskOguid && !envelope.isRead
    ? [
      {
        data: MARK_AS_READ_ACTION,
        name: MARK_AS_READ_ACTION,
        translation: t('envelopes:actions:markAsRead'),
      },
      ...actionDropdownItems,
    ]
    : actionDropdownItems;

  const openMassActionModal = (action: EnvelopeAllovedActions): void => {
    setSelectedMassAction(action);
  };

  const handleMassActionClose = (reload?: boolean): void => {
    setSelectedMassAction(null);

    if (reload) {
      loadEnvelopes();
    }
  };

  const executeTaskComplete = async (): Promise<void> => {
    if (!envelope.currentUserTaskOguid || processingAction) return;

    setProcessingAction(true);

    try {
      await TasksService.postTaskComplete(envelope.currentUserTaskOguid, null);
      await loadEnvelopes();
    } catch (error: any) {
      displayErrorNotification(error);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleSelectDropDown = (option: ILibDropdownItem): void => {
    if (option.data === MARK_AS_READ_ACTION) {
      handleMarkAsRead();
      return;
    }

    handleClickAllowedActions({ direction: envelopeDirection, option, oguid, subject, boxOguid });
  };

  const handleMarkAsRead = (): void => {
    if (!envelope.currentUserTaskOguid || envelope.isRead || processingAction) return;

    setProcessingAction(true);

    TasksService.postTasksMarkIsRead([envelope.currentUserTaskOguid], true)
      .then(() => {
        dispatch(setEnvelopeReadState({
          boxOguid: envelope.box.oguid,
          envelopeOguid: envelope.oguid,
          isRead: true,
        }));
      })
      .catch(displayErrorNotification)
      .finally(() => setProcessingAction(false));
  };

  const handleClickButton = (): void => {
    const option = { name: allowedActionValue };
    const massAction = allowedActionValue as unknown as EnvelopeAllovedActions;

    if (
      massAction === EnvelopeAllovedActions.APPROVE &&
      isSingleEnvelopeMassActionAvailable(envelope, massAction, instance)
    ) {
      executeTaskComplete();
      return;
    }

    if (massAction === EnvelopeAllovedActions.SIGN && isRowSignMassActionAvailable) {
      openMassActionModal(massAction);
      return;
    }

    if (allowedActionValue === EnvelopeAllowedActionsButton.SIGN && boxOguid) {
      dispatch(getEnvelope({ direction: envelopeDirection, oguid, boxOguid }))
        .unwrap()
        .then((resp) => {
          if (resp) {
            handleClickAllowedActions({
              option,
              oguid,
              direction: envelopeDirection,
              isQualifiedSignature: getEnvelopeQualifiedSignature(
                resp,
                currentUserBoxOguid,
                instance
              ),
              boxOguid,
            });
          }
        })
        .catch(catchThunkError);
    } else {
      handleClickAllowedActions({ direction: envelopeDirection, option, oguid, boxOguid });
    }
  };

  return (
    <td>
      <ComboButton
        selectedTitle={t(title)}
        buttonTheme={theme}
        containerClasses={styles.actionButton}
        dropdownItems={dropdownItems}
        itemClasses='py-1'
        size='small'
        horizontalPosition={ILibHorizontalPosition.RIGHT}
        onSelect={handleSelectDropDown}
        allowDropdownFlip
        correctViewport={{ bottom: 48 }}
        onClick={handleClickButton}
        loading={processingAction}
        isDisabled={processingAction}
      />
      {selectedMassAction && (
        <EnvelopeMassActionModal
          action={selectedMassAction}
          skipReview
          selectedEnvelopes={[envelope]}
          onCloseModal={handleMassActionClose}
          onTasksUpdated={loadEnvelopes}
        />
      )}
    </td>
  );
};

export default EnvelopeActions;
